'use client';
import React, { useState, useEffect, useCallback } from 'react';
import Image from 'next/image';
import { iconMap, type IconName, type IconStatus } from './icon-map';

export interface IconProps {
  /** 图标名称 */
  name: IconName;
  /** 图标状态，默认为 'default' */
  status?: IconStatus;
  /** 图片的alt属性 */
  alt?: string;
  /** 自定义className */
  className?: string;
  /** 自定义样式 */
  style?: React.CSSProperties;
  /** 点击事件 */
  onClick?: (e: React.MouseEvent) => void;
  /** 图标宽度，默认为24 */
  width?: number;
  /** 图标高度，默认为24 */
  height?: number;
  /** 图片加载完成回调 */
  onLoaded?: () => void;
}

const Icon: React.FC<IconProps> = ({
  name,
  status = 'default',
  alt,
  className,
  style,
  onClick,
  width = 24,
  height = 24,
  onLoaded,
}) => {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [hasError, setHasError] = useState(false);

  // 动态加载图片
  const loadImage = useCallback(async () => {
    try {
      setHasError(false);

      const iconConfig = iconMap[name];
      if (!iconConfig) {
        console.warn(`Icon "${name}" not found`);
        setHasError(true);
        return;
      }

      // 如果请求的状态不存在，则使用默认状态
      const imageImport = iconConfig[status] || iconConfig.default;
      if (!imageImport) {
        console.warn(`Icon "${name}" with status "${status}" not found`);
        setHasError(true);
        return;
      }

      const imageModule = await imageImport();
      const imageSrc = typeof imageModule.default === 'string'
        ? imageModule.default
        : imageModule.default.src;
      setImageSrc(imageSrc);
      onLoaded?.();
    } catch (error) {
      console.warn(`Failed to load icon "${name}" with status "${status}":`, error);
      setHasError(true);
    }
  }, [name, status]);

  // 组件挂载时加载图片
  useEffect(() => {
    loadImage();
  }, [loadImage]);

  // 错误状态
  if (hasError || !imageSrc) {
    return null;
  }

  return (
    <Image
      src={imageSrc}
      alt={alt || name}
      className={className}
      style={style}
      onClick={onClick}
      width={width}
      height={height}
      onError={() => setHasError(true)}
    />
  );
};

Icon.displayName = 'Icon';

export default Icon;
